/**
 * UnifiedAIPanel -- Monarch AI right sidebar chat panel
 *
 * When open: fixed right sidebar that pushes main content left.
 * Three widths: narrow (380px), medium (480px), wide (600px).
 * When closed: floating pill button at bottom-right.
 *
 * State managed via chatStore (Zustand). No external hooks --
 * sendAnalyzeQuery handles the full send->response cycle.
 */

import { useCallback, useEffect } from 'react';
import { useChatStore, PANEL_WIDTH_PX, type PanelWidth } from '@/stores/chatStore';
import MessageList from '../chat/MessageList';
import ChatInput from '../chat/ChatInput';

// ---------------------------------------------------------------------------
// Icons (inline SVG -- no dependencies)
// ---------------------------------------------------------------------------

function SparklesIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function NewChatIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShrinkIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <path d="M6 2v4H2M10 2v4h4M14 10h-4v4M2 10h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Suggested Prompts
// ---------------------------------------------------------------------------

const SUGGESTED_PROMPTS = [
  {
    id: 'booking-funnel',
    label: 'Analyze booking funnel',
    prompt: 'How is our booking funnel performing? We see 13K widget clicks but only 118 form submissions.',
  },
  {
    id: 'ota-vs-direct',
    label: 'Direct vs OTA traffic',
    prompt: 'Compare direct vs OTA traffic quality for The Monarch.',
  },
  {
    id: 'top-pages',
    label: 'Top reservation pages',
    prompt: 'Which pages drive the most reservations and booking intent?',
  },
  {
    id: 'weekend-patterns',
    label: 'Weekend vs weekday',
    prompt: 'Analyze our weekend vs weekday traffic patterns.',
  },
  {
    id: 'dropoff',
    label: 'Booking widget drop-off',
    prompt: "What's driving the 99% drop-off at the booking widget?",
  },
  {
    id: 'geography',
    label: 'Guest geography',
    prompt: 'Show geography breakdown of our website visitors.',
  },
];

function SuggestedPrompts({ onSelect }: { onSelect: (prompt: string) => void }) {
  return (
    <div className="px-4 sm:px-6 py-6 sm:py-8 flex-1 flex flex-col items-center justify-center bg-[var(--color-ai-panel-bg)]">
      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl mb-4 sm:mb-5 bg-bg-2 border border-border flex items-center justify-center">
        <SparklesIcon className="w-6 h-6 sm:w-7 sm:h-7 text-monarch-gold" />
      </div>
      <h3 className="text-sm sm:text-base font-bold text-text-2 mt-2 mb-1.5 tracking-wide font-display">
        Monarch AI
      </h3>
      <p className="text-xs text-text-3 text-center max-w-sm mb-6 sm:mb-8 leading-relaxed">
        Connected to GA4 with real hotel performance data.
      </p>
      <div className="w-full max-w-md">
        <div className="text-[10px] font-semibold text-text-3 mb-3 uppercase tracking-wider px-1">
          Suggested queries
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SUGGESTED_PROMPTS.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelect(item.prompt)}
              className="
                inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-left
                bg-bg-card border border-border
                text-text-2 text-xs leading-snug
                hover:bg-monarch-gold hover:text-white hover:border-monarch-gold
                transition-colors duration-150
              "
            >
              <svg className="w-3 h-3 flex-shrink-0 opacity-40" viewBox="0 0 12 12" fill="none">
                <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel Width Config
// ---------------------------------------------------------------------------

const PANEL_WIDTHS: Record<PanelWidth, string> = {
  narrow: `w-[${PANEL_WIDTH_PX.narrow}px]`,
  medium: `w-[${PANEL_WIDTH_PX.medium}px]`,
  wide: `w-[${PANEL_WIDTH_PX.wide}px]`,
};

const WIDTH_CYCLE: PanelWidth[] = ['narrow', 'medium', 'wide'];

// ---------------------------------------------------------------------------
// UnifiedAIPanel
// ---------------------------------------------------------------------------

export default function UnifiedAIPanel() {
  const isOpen = useChatStore((s) => s.isOpen);
  const messages = useChatStore((s) => s.messages);
  const isThinking = useChatStore((s) => s.isThinking);
  const panelWidth = useChatStore((s) => s.panelWidth);

  const closeChat = useCallback(() => {
    useChatStore.getState().setOpen(false);
  }, []);

  const openChat = useCallback(() => {
    useChatStore.getState().setOpen(true);
  }, []);

  const handleNewConversation = useCallback(() => {
    useChatStore.getState().clearMessages();
  }, []);

  const cycleWidth = useCallback(() => {
    const currentIdx = WIDTH_CYCLE.indexOf(panelWidth);
    const nextIdx = (currentIdx + 1) % WIDTH_CYCLE.length;
    useChatStore.getState().setPanelWidth(WIDTH_CYCLE[nextIdx]);
  }, [panelWidth]);

  const handleSend = useCallback((content: string) => {
    useChatStore.getState().sendAnalyzeQuery(content);
  }, []);

  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeChat();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeChat]);

  // Width label for tooltip
  const nextWidth = WIDTH_CYCLE[(WIDTH_CYCLE.indexOf(panelWidth) + 1) % WIDTH_CYCLE.length];
  const isWideOrMedium = panelWidth === 'wide' || panelWidth === 'medium';

  // ---- Collapsed: floating pill button ----
  if (!isOpen) {
    return (
      <button
        onClick={openChat}
        className="
          hidden md:flex
          fixed bottom-6 right-6 z-[90]
          items-center gap-2
          px-5 py-3 rounded-xl
          bg-bg-card border border-border
          shadow-none
          hover:border-monarch-gold hover:bg-bg-2
          transition-all duration-200
          group
        "
        aria-label="Open AI assistant"
      >
        <SparklesIcon className="w-[18px] h-[18px] text-monarch-gold group-hover:text-monarch-gold/80 transition-colors" />
        <span className="text-sm font-medium font-display text-monarch-gold group-hover:text-monarch-gold transition-colors tracking-wide uppercase">
          Monarch AI
        </span>
      </button>
    );
  }

  // ---- Expanded: right sidebar (desktop) / full-screen overlay (mobile) ----
  return (
    <aside
      className={`
        fixed right-0 z-[45]
        max-md:top-0 max-md:bottom-0 max-md:left-0
        md:top-16 md:bottom-0
        ${PANEL_WIDTHS[panelWidth]}
        max-md:w-full max-md:min-w-0 max-md:max-w-none
        bg-[var(--color-ai-panel-bg)] border-l border-border
        flex flex-col
        transition-all duration-300 ease-out
      `}
      style={{ width: window.innerWidth >= 768 ? `${PANEL_WIDTH_PX[panelWidth]}px` : undefined }}
    >
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between px-4 py-3 md:py-3 border-b border-border bg-bg-2 flex-shrink-0">
        {/* Mobile: back arrow + title */}
        <div className="flex items-center gap-3">
          <button
            onClick={closeChat}
            className="md:hidden p-1.5 -ml-1 rounded text-text-3 hover:text-text-2 transition-colors"
            aria-label="Close chat"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="flex items-center gap-2.5">
            <SparklesIcon className="w-4 h-4 text-monarch-gold" />
            <h2 className="text-sm font-bold font-display text-monarch-gold tracking-wider uppercase">
              Monarch AI
            </h2>
            {/* Status indicator */}
            <span
              className={`w-2 h-2 rounded-full transition-colors ${
                isThinking
                  ? 'bg-monarch-gold animate-pulse'
                  : 'bg-success'
              }`}
            />
            {isThinking && (
              <span className="text-[10px] text-monarch-gold font-medium">Processing...</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* New chat */}
          {messages.length > 0 && (
            <button
              onClick={handleNewConversation}
              className="p-2 rounded-lg text-text-3 hover:text-text-2 hover:bg-bg-card transition-colors border border-transparent hover:border-border"
              title="New conversation"
            >
              <NewChatIcon />
            </button>
          )}

          {/* Toggle width (narrow/medium/wide) -- hidden on mobile */}
          <button
            onClick={cycleWidth}
            className="hidden md:block p-2 rounded-lg text-text-3 hover:text-text-2 hover:bg-bg-card transition-colors border border-transparent hover:border-border"
            title={`Switch to ${nextWidth} width`}
          >
            {isWideOrMedium ? <ShrinkIcon /> : <ExpandIcon />}
          </button>

          {/* Close -- desktop only (mobile uses back arrow) */}
          <button
            onClick={closeChat}
            className="hidden md:block p-2 rounded-lg text-text-3 hover:text-text-2 hover:bg-bg-card transition-colors border border-transparent hover:border-border"
            title="Close panel"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* ---- Message area / Suggested prompts ---- */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {messages.length === 0 ? (
          <SuggestedPrompts onSelect={handleSend} />
        ) : (
          <MessageList messages={messages} />
        )}

        {/* Thinking indicator bar */}
        {isThinking && messages.length > 0 && (
          <div className="px-4 py-2.5 border-t border-border bg-bg-2 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-monarch-gold rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-monarch-gold rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-monarch-gold rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-xs text-text-3 font-medium">
                Analyzing your data...
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ---- Input ---- */}
      <div className="pb-[env(safe-area-inset-bottom,0px)]">
        <ChatInput
          onSend={handleSend}
          isThinking={isThinking}
          placeholder="Ask about traffic, bookings, guest engagement..."
        />
      </div>
    </aside>
  );
}
